import {ref} from "vue";
import {tt} from '@/i18n';
import {useRoute} from "vue-router";

let emojiFlags = require('emoji-flags');

export default function useHelperLibrary() {
    const route = useRoute();

    const getContentTypeTitle = (content) => {
        switch (content.quest_type_key) {
            case 'QUEST':
                return tt('content_card__type_title_quest');
            case 'PATH':
                return tt('content_card__type_title_path');
            case 'CONTENT':
                switch (content.content_type_vertical) {
                    case 'VIDEO':
                        return tt('content_card__type_title_content_video');
                    case 'AUDIO':
                        return tt('content_card__type_title_content_audio');
                    case 'DOCUMENT':
                        return tt('content_card__type_title_content_document');
                    case 'INTERACTIVE CONTENT':
                        return tt('content_card__type_title_content_scorm');
                    case 'EXTERNAL CONTENT':
                        return tt('content_card__type_title_content_website');
                    case 'QUIZ':
                        return tt('content_card__type_title_content_quiz');
                    default:
                        return '';
                }
            default:
                return '';
        }
    }

    const getQuestButtonLabel = (quest) => {
        if (quest.locked ?? false) {
            return tt('content_card__button_quest_locked');
        }
        else if (quest.progress === 0) {
            return tt('content_card__button_quest_start');
        }
        else if (quest.progress === 100) {
            return tt('content_card__button_quest_review');
        }
        else {
            return tt('content_card__button_quest_continue');
        }
    }

    const getContentButtonLabel = (vertical) => {
        switch (vertical) {
            case 'VIDEO':
                return tt('content_card__button_content_video');
            case 'AUDIO':
                return tt('content_card__button_content_audio');
            case 'DOCUMENT':
                return tt('content_card__button_content_document');
            case 'INTERACTIVE CONTENT':
                return tt('content_card__button_content_scorm');
            case 'EXTERNAL CONTENT':
                return tt('content_card__button_content_website');
            case 'QUIZ':
                return tt('content_card__button_content_quiz');
            default:
                return '';
        }
    }

    const getButtonClass = (classes, content, index) => {
        const isActive = ref(false)

        let color = '';
        let disabled = '';

        if (isActive.value) {
            disabled = 'animate-pulse opacity-20 cursor-not-allowed'
        }

        if (content.progress === 0) {
            color = 'bg-blue-600'
        }

        if (content.progress === 100) {
            color = 'bg-[#FFC90D]'
        }

        if (content.progress > 0 && content.progress < 100) {
            color = 'bg-green-600'
        }

        if (content.locked) {
            color = 'bg-gray-600/20'
        }

        return classes + ' ' + disabled + ' ' + color;
    }

    const getComplianceDaysLeftTitle = (days_left) => {
        if (days_left == 0) {
            return tt('content_card__details_due_date_today');
        }

        if (days_left == 1) {
            return tt('content_card__details_due_date_tomorrow');
        }

        if (days_left < 0) {
            if (Math.abs(days_left) == 1) {
                return tt('content_card__details_due_date_overdue_1');
            }
            else {
                return tt('content_card__details_due_date_overdue_n', {days: Math.abs(days_left)});
            }
        }

        return tt('content_card__details_due_date_left', {days: Math.abs(days_left)});
    }

    const getComplianceDateFormat = (date) => {
        const dateObject = new Date(date);
        const options = { month: "long", day: "numeric", year: "numeric" };
        return new Intl.DateTimeFormat("en-US", options).format(dateObject);
    }

    const getComplianceIconColor = (days_left) => {
        let color = 'stroke-orange-600';

        if (days_left < 2) {
            color = 'stroke-amber-900';
        }

        return color;
    }

    const getComplianceTextColor = (days_left) => {
        let color = 'text-orange-800';

        if (days_left < 0) {
            color = 'text-red-800';
        }

        return color;
    }

    const getContentStatusClass = (progress) => {
        if (progress === 0) {
            return 'text-blue-700'
        }

        if (progress === 100) {
            return 'text-[#FFC90D]'
        }

        return 'text-green-700'
    }

    const getContentStatusText = (progress) => {
        if (progress === 0) {
            return tt('content_card__status_not_started');
        }

        if (progress === 100) {
            return tt('content_card__status_completed');
        }

        return tt('content_card__status_in_progress');
    }

    const getLockContentLabel = (type, vertical) => {
        if (type === 'QUEST') {
            return tt('content_card__type_title_quest');
        }
        else { //content
            switch (vertical) {
                case 'VIDEO':
                    return tt('content_card__type_title_content_video');
                case 'AUDIO':
                    return tt('content_card__type_title_content_audio');
                case 'DOCUMENT':
                    return tt('content_card__type_title_content_document');
                case 'INTERACTIVE CONTENT':
                    return tt('content_card__type_title_content_scorm');
                case 'EXTERNAL CONTENT':
                    return tt('content_card__type_title_content_website');
                case 'QUIZ':
                    return tt('content_card__type_title_content_quiz');
            }
        }

        return tt('content_card__type_title_content');
    }

    const setFavicon = (brand) => {
        if (brand && brand.favicon) {
            const general = document.getElementById("favicon");
            const apple = document.getElementById("apple");
            const android = document.getElementById("android");
            general.href = brand.favicon.general;
            apple.href = brand.favicon.apple;
            android.href = brand.favicon.android;
        }
    }

    function computeButtonClass(quest) {
        const isActive = ref(false)

        let color, disabled;

        if (isActive.value) {
            disabled = 'animate-pulse opacity-20 cursor-not-allowed'
        }

        if (quest.progress === 0) {
            color = 'bg-blue-600'
        }

        if (quest.progress === 100) {
            color = 'bg-[#FFC90D]'
        }

        if (quest.progress > 0 && quest.progress < 100) {
            color = 'bg-green-600'
        }

        if (quest.locked) {
            color = 'bg-gray-600/20'
        }

        return disabled + ' ' + color
    }

    function computeRawStatus(content) {
        if (content.progress === 0) {
            return "not_started";
        }

        if (content.progress === 100) {
            return "completed";
        }

        return "incomplete";
    }

    function countryCodeToFlagEmoji(countryCode) {
        if (countryCode == 'CHT') { //hack for traditional chinese
            countryCode = 'CN';
        }
        let data = emojiFlags.countryCode(countryCode) ;
        return (data && data.emoji) || '🌐';
    }

    function languageCodeToFlagEmoji(languageCode) {
        const parts = languageCode.split("-");
        const countryCode = parts[1] || parts[0];
        return countryCodeToFlagEmoji(countryCode);
    }

    const setDocumentTitle = () => {
        if (route.path === '/') {
            document.title = tt('tab_title__my_portal');
        }
        else if (route.path === '/library') {
            document.title = tt('tab_title__content_library');
        }
    }

    function urlEncode(str) {
        var histogram = {}, unicodeStr = '', hexEscStr = '';
        var ret = (str + '').toString();

        var replacer = function (search, replace, str) {
            var tmp_arr = [];
            tmp_arr = str.split(search);
            return tmp_arr.join(replace);
        };

        // The histogram is identical to the one in urldecode.
        histogram["'"] = '%27';
        histogram['('] = '%28';
        histogram[')'] = '%29';
        histogram['*'] = '%2A';
        histogram['~'] = '%7E';
        histogram['!'] = '%21';
        histogram['%20'] = '+';
        histogram['\u00DC'] = '%DC';
        histogram['\u00FC'] = '%FC';
        histogram['\u00C4'] = '%D4';
        histogram['\u00E4'] = '%E4';
        histogram['\u00D6'] = '%D6';
        histogram['\u00F6'] = '%F6';
        histogram['\u00DF'] = '%DF';
        histogram['\u20AC'] = '%80';
        histogram['\u0081'] = '%81';
        histogram['\u201A'] = '%82';
        histogram['\u0192'] = '%83';
        histogram['\u201E'] = '%84';
        histogram['\u2026'] = '%85';
        histogram['\u2020'] = '%86';
        histogram['\u2021'] = '%87';
        histogram['\u02C6'] = '%88';
        histogram['\u2030'] = '%89';
        histogram['\u0160'] = '%8A';
        histogram['\u2039'] = '%8B';
        histogram['\u0152'] = '%8C';
        histogram['\u008D'] = '%8D';
        histogram['\u017D'] = '%8E';
        histogram['\u008F'] = '%8F';
        histogram['\u0090'] = '%90';
        histogram['\u2018'] = '%91';
        histogram['\u2019'] = '%92';
        histogram['\u201C'] = '%93';
        histogram['\u201D'] = '%94';
        histogram['\u2022'] = '%95';
        histogram['\u2013'] = '%96';
        histogram['\u2014'] = '%97';
        histogram['\u02DC'] = '%98';
        histogram['\u2122'] = '%99';
        histogram['\u0161'] = '%9A';
        histogram['\u203A'] = '%9B';
        histogram['\u0153'] = '%9C';
        histogram['\u009D'] = '%9D';
        histogram['\u017E'] = '%9E';
        histogram['\u0178'] = '%9F';

        // Begin with encodeURIComponent, which most resembles PHP's encoding functions
        ret = encodeURIComponent(ret);

        for (unicodeStr in histogram) {
            hexEscStr = histogram[unicodeStr];
            ret = replacer(unicodeStr, hexEscStr, ret);
        }

        // Uppercase for full PHP compatibility
        return ret.replace(/(%([a-z0-9]{2}))/g, function (full, m1, m2) {
            return "%" + m2.toUpperCase();
        });
    }

    function isMobile() {
        let check = false;
        (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
        return check;
    }

    function isSafari() {
        return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    }

    return {
        getContentTypeTitle,
        getQuestButtonLabel,
        getContentButtonLabel,
        getButtonClass,
        getComplianceDaysLeftTitle,
        getComplianceDateFormat,
        getComplianceIconColor,
        getComplianceTextColor,
        getContentStatusClass,
        getContentStatusText,
        getLockContentLabel,
        setFavicon,
        computeButtonClass,
        computeRawStatus,
        urlEncode,
        languageCodeToFlagEmoji,
        setDocumentTitle,
        isMobile,
        isSafari,
    };
}